/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { COLUMNTYPE_NUMBER } from 'constants';

import React from 'react';
import PropTypes from 'prop-types';
import { Select, SelectItem, FormItem, FormLabel } from 'carbon-addons-iot-react';
import { useField } from 'formik';
import { injectIntl } from 'react-intl';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';

import { handleFormat } from 'formFunctions';
import { FormLabelTooltip } from 'Common';

const propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  hideLabel: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
      value: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
    })
  ).isRequired,
  tooltip: PropTypes.string,
  learnMore: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func, // TODO: need to think about locale parsing for numbers
  className: PropTypes.string,
  tooltipDirection: PropTypes.string,
  defaultValue: PropTypes.string,
};

const defaultProps = {
  disabled: false,
  onChange: noop,
  label: null,
  required: true,
  hideLabel: false,
  learnMore: null,
  tooltip: null,
  className: null,
  tooltipDirection: null,
  defaultValue: null,
};

/** The value should already be set from formik */
const SelectFormik = ({
  id,
  disabled,
  options,
  label,
  hideLabel,
  learnMore,
  tooltip,
  required,
  onChange,
  className,
  tooltipDirection,
  defaultValue,
  intl: { locale },
  ...others
}) => {
  const [field, meta] = useField({ name: id });
  return (
    <FormItem className={className}>
      {!hideLabel ? (
        <FormLabel>
          <FormLabelTooltip
            tooltipDirection={tooltipDirection}
            label={label}
            tooltip={tooltip}
            learnMore={learnMore}
            required={required}
          />
        </FormLabel>
      ) : null}
      <Select
        id={id}
        name={id}
        hideLabel
        labelText={label}
        onChange={e => {
          field.onChange(e);
          // and pass up the event in case the container components needs to react to this event in some way
          // eslint-disable-next-line no-unused-expressions
          if (onChange) {
            onChange(e);
          } // TODO: need to implement custom parse for numeric values once Formik supports it: https://github.com/formium/formik/issues/1525
        }}
        onBlur={field.onBlur}
        value={!isNil(field.value) && field.value !== '' ? field.value : defaultValue}
        invalid={Boolean(meta.error)}
        invalidText={meta.error}
        disabled={disabled}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...others}>
        {options.map(({ label: optionLabel, value }) => (
          <SelectItem
            key={`id-${optionLabel + value}`}
            text={handleFormat(
              optionLabel,
              typeof optionLabel === 'number' ? COLUMNTYPE_NUMBER : null,
              locale
            )}
            value={value}
          />
        ))}
      </Select>
    </FormItem>
  );
};

SelectFormik.propTypes = propTypes;
SelectFormik.defaultProps = defaultProps;

export default injectIntl(SelectFormik);
